import * as React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useAIStreaming } from "@editor/providers/AIStreamingProvider";
import {
  selectLeftBarActiveTab,
  selectLeftBarWidth,
  setLeftBarActiveTab,
} from "@editor/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@editor/store";
import useCreateComponentForm from "@hooks/useCreateComponentForm";

import twMerge from "@replo/design-system/utils/twMerge";

import LeftBarHeader from "./LeftBarHeader";
import LeftBarPanel from "./LeftBarPanel";

interface LeftBarProps {
  showElementSpecificContent: boolean;
  fullPageOverlay?: React.ReactNode;
}

const LeftBar: React.FC<LeftBarProps> = React.memo(function LeftBar({
  showElementSpecificContent,
  fullPageOverlay,
}: LeftBarProps) {
  const { isLoading } = useCurrentProjectContext();
  const dispatch = useEditorDispatch();
  const isFullPageOverlay = Boolean(fullPageOverlay);
  // Note (Evan, 2024-10-08): This hook ~will~ re-render with each streamed action,
  // but it's not an issue because we're just returning null while streaming. There is
  // a more global issue with this, which see: https://replohq.slack.com/archives/C03AACVP08Y/p1728413180369459.
  const { hasQueuedGeneration } = useAIStreaming();
  const leftBarActiveTab = useEditorSelector(selectLeftBarActiveTab);
  const leftBarWidth = useEditorSelector(selectLeftBarWidth);
  const isLeftBarOpen = leftBarActiveTab !== null;

  useCreateComponentForm(() => dispatch(setLeftBarActiveTab("components")));

  if (isLoading || hasQueuedGeneration) {
    return null;
  }
  if (isFullPageOverlay) {
    return (
      <div
        className="bg-neutral-soft absolute h-full w-full"
        style={{ top: 0, left: 0 }}
      >
        <LeftBarHeader
          showElementSpecificContent={showElementSpecificContent}
          isFullPageOverlay={isFullPageOverlay}
        />
        <LeftBarPanel showElementSpecificContent={showElementSpecificContent} />
        <div
          className={twMerge(
            "transition-[margin-left]",
            isLeftBarOpen ? "" : "ml-0",
          )}
          style={{
            marginLeft: isLeftBarOpen ? `${leftBarWidth}px` : "0",
          }}
        >
          {fullPageOverlay}
        </div>
      </div>
    );
  }

  return (
    <>
      <LeftBarHeader
        showElementSpecificContent={showElementSpecificContent}
        isFullPageOverlay={isFullPageOverlay}
      />
      <LeftBarPanel showElementSpecificContent={showElementSpecificContent} />
    </>
  );
});

export default LeftBar;
